import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Car } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { RegistrationInputButton } from "@/components/valuation/RegistrationInputButton";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

interface ValuationBoxProps {
  darkOverlayOpacity: number;
  onValuationClick: () => void;
}

const ValuationBox = ({
  darkOverlayOpacity,
  onValuationClick,
}: ValuationBoxProps) => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const valuationData =
    labels.menuPages?.sellCar?.contactSection?.valuationBox || {};
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCreateAccount = () => {
    if (!user) {
      navigate("/auth");
    }
  };
  return (
    <motion.div
      id="valuation-section"
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="max-w-2xl mx-auto mb-16"
    >
      <motion.div
        className="bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl"
        animate={{
          boxShadow:
            darkOverlayOpacity > 0.5
              ? "0 25px 50px -12px rgba(139, 92, 246, 0.6)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-center mb-6">
          <Car className="w-12 h-12 text-white mr-4" />
          <h3 className="text-3xl font-bold">
            {valuationData.title || "Värdera Din Bil"}
          </h3>
        </div>
        <p className="text-xl mb-8 opacity-90 text-center">
          {valuationData.description ||
            "Få en professionell värdering av din bil och kom igång med försäljningsprocessen idag."}
        </p>

        <div className="space-y-6">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
            <h4 className="text-xl font-semibold mb-4 text-center">
              {valuationData.includesTitle || "Vad ingår i värderingen:"}
            </h4>
            <ul className="space-y-2">
              {(valuationData.includes || []).map(
                (item: string, index: number) => (
                  <li key={index} className="flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                    {item}
                  </li>
                ),
              )}
            </ul>
          </div>

          <div className="mb-8">
            <RegistrationInputButton onClick={onValuationClick} />
          </div>

          {!user && (
            <Button
              onClick={handleCreateAccount}
              className="w-full bg-white text-violet-600 hover:bg-gray-100 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {valuationData.createAccount || "Skapa Konto"}
            </Button>
          )}

          <p className="text-sm opacity-80 text-center">
            {valuationData.disclaimer ||
              "Kostnadsfri värdering utan förpliktelser"}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ValuationBox;
